import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { baseGrid, siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { contentStyles, link } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import TextboxStyles from '@stories/Components/Misc/Forms/Inputs/Textbox/Textbox.styles';
import SocialLinksStyles from '@stories/Components/Misc/SocialLinks/SocialLinks.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const Container = styled.footer `
  --bgColour: ${brand.grey.dark};
  --fgColour: ${brand.primary.white};

  color: var(--fgColour);
  background-color: var(--bgColour);
  padding: var(--sectionSpacing) 0;
`;
const Inner = styled.div `
  ${siteWide};
  ${baseGrid};
`;
const TopWrapper = styled.div `
  grid-column: 1 / span 12;
  display: flex;
  gap: 32px;
  justify-content: space-between;
  margin: 0 0 ${fluid(40, 52)};
`;
const PromotionText = styled.div `
  ${contentStyles};

  max-width: ${fluid(600, 1030, Device.Tablet)};

  p {
    font-size: ${fluid(16, 32)};
    line-height: ${fluid(24, 42)};
  }
`;
const BackToTopButton = styled.button `
  ${ButtonReset};
  ${fonts.poppins.extraBold};

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  font-size: ${fluid(10, 12)};
  line-height: 12px;
  color: currentColor;
  margin: 0 0 auto auto;
  white-space: nowrap;

  &:focus-visible {
    outline: auto;
    outline-offset: 5px;
    outline-width: 1px;
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }
`;
const BackToTopIcon = styled.span `
  display: grid;
  place-content: center;
  width: ${fluid(40, 56)};
  aspect-ratio: 1;
  border: 2px solid currentColor;
  border-radius: 5000px;

  svg {
    ${transition('transform', 0.25)};

    display: block;
    width: ${fluid(14, 20)};
    transform: rotate(-90deg);

    ${BackToTopButton}:hover &,
    ${BackToTopButton}:focus-visible & {
      transform: translateY(-12.5%) rotate(-90deg);
    }

    /* iOS only, move arrow up slightly to fix vertical aligment */
    @media ${until(Device.TabletLarge)} {
      @supports (-webkit-touch-callout: none) {
        transform: translateY(-2px) rotate(-90deg);
      }
    }
  }
`;
const NewsletterForm = styled.div `
  --textColour: var(--fgColour);

  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${fluid(16, 32)};
  grid-column: 1 / span 12;
  margin: 0 0 32px;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 6;
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: 1 / span 3;
  }

  form {
    position: relative;
  }

  ${TextboxStyles.Input} {
    --bgColour: var(--bgColour);
    --borderColour: var(--textColour);

    border-width: 2px;
    border-radius: 5000px;
  }

  ${ButtonStyles.Container} {
    --borderColour: transparent;

    border-radius: 5000px;
    height: 50px;
    width: 50px;
    position: absolute;
    top: 0;
    right: 0;
  }
`;
const Logo = styled.a `
  display: block;
  aspect-ratio: 141 / 40;
  max-width: 141px;

  svg {
    display: block;
  }
`;
const NewsletterText = styled.div `
  ${contentStyles};

  p {
    margin-bottom: 8px;

    &.small {
      font-size: ${fluid(14, 16)};
      line-height: ${fluid(22, 26)};
    }
  }
`;
const LinkListContainer = styled.div `
  grid-column: 1 / span 12;
  display: flex;
  flex-direction: column;
  column-gap: ${fluid(32, 60)};

  > div {
    flex: 1 1;
  }

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: 5 / span 8;
  }
`;
const footerLinkStyles = css `
  ${link};

  --textDecorationColour: transparent;

  &:focus-visible,
  &:hover {
    text-decoration-color: currentColor;
  }
`;
const LinkListHeader = styled.button `
  ${ButtonReset};

  color: currentColor;
  margin: 0 0 ${fluid(12, 16)};

  h3 {
    ${fonts.poppins.extraBold};

    font-size: ${fluid(15, 20)};
    line-height: 1.2;
  }

  button& {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    gap: 24px;
    justify-content: space-between;
    padding: 0 0 15px;
    border-bottom: 1px solid currentColor;

    &:focus-visible {
      outline: auto;
      outline-offset: 5px;
      outline-width: 1px;
    }

    &[data-open='true'] svg {
      transform: rotate(180deg);
    }

    /* Mobile click styles - button should mimic press */
    @media ${until(Device.TabletLarge)} {
      ${transition('transform', 0.15)};

      &:active {
        transform: translateY(1.5px);
      }
    }

    @media ${from(Device.TabletLarge)} {
      display: none;
    }
  }

  a& {
    ${footerLinkStyles};
    display: block;
  }

  a&,
  span& {
    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }

  svg {
    ${transition('transform', 0.25)};

    display: block;
    width: 12px;
  }
`;
const LinkList = styled(m.ul) `
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;

  li {
    ${fonts.poppins.regular};

    font-size: ${fluid(14, 16)};
    line-height: ${fluid(18, 20)};
  }

  a {
    ${footerLinkStyles};
  }

  @media ${from(Device.TabletLarge)} {
    height: auto !important;
    padding-bottom: 3px;
    margin-bottom: 0 !important;
  }
`;
const BottomWrapper = styled.div `
  --fontSize: ${fluid(12, 14)};
  --lineHeight: ${fluid(20, 24)};

  grid-column: 1 / span 12;
  display: flex;
  flex-direction: column;
  gap: 32px 46px;
  margin: ${fluid(32, 96)} 0 0;

  ${SocialLinksStyles.Container} {
    --fgColour: currentColor;
  }

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
    justify-content: space-between;
    padding-top: ${fluid(32, 48)};
    border-top: 1px solid currentColor;
  }
`;
const CopyrightText = styled.div `
  ${contentStyles};

  flex: 1 1;
  max-width: 372px;

  p {
    font-size: var(--fontSize);
    line-height: var(--lineHeight);
    margin-bottom: ${fluid(8, 12)};
  }

  @media ${until(Device.TabletLarge)} {
    text-align: center;
    order: 3;
    margin: 0 auto;
  }
`;
const AffiliateLogos = styled.div `
  display: inline-flex;
  justify-content: center;
  gap: ${fluid(32, 40)};
  height: ${fluid(56, 80)};
  margin: 0 auto;

  a,
  span {
    display: inline-flex;

    img {
      margin: auto;
      max-height: 100%;
    }

    @media ${from(Device.Tablet)} {
      max-width: ${fluid(114, 142)};
    }
  }
`;
const SocialLegalLinks = styled.div `
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  @media ${until(Device.TabletLarge)} {
    order: 2;
  }

  @media ${from(Device.TabletLarge)} {
    align-items: flex-end;
    margin-left: auto;
  }
`;
const LegalLinks = styled.ul `
  ${fonts.poppins.regular};

  display: flex;
  gap: 16px;
  justify-content: center;
  font-size: var(--fontSize);
  line-height: var(--lineHeight);

  a {
    ${footerLinkStyles};
  }

  button {
    ${ButtonReset};
    ${footerLinkStyles};
    ${fonts.poppins.regular};
  }
`;
const FooterStyles = {
    AffiliateLogos,
    BackToTopButton,
    BackToTopIcon,
    BottomWrapper,
    Container,
    CopyrightText,
    Inner,
    LegalLinks,
    LinkListContainer,
    Logo,
    NewsletterForm,
    NewsletterText,
    PromotionText,
    SocialLegalLinks,
    TopWrapper,
    LinkListHeader,
    LinkList,
};
export default FooterStyles;
